import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { applicationInsights } from '../../../ApplicationInsights';
import { BrokerApi, UsersApi, FileApi } from '@apply/api';

type IBrokerAdminResponse = BrokerApi.IBrokerAdminResponse;
type IBrokerResponse = BrokerApi.IBrokerResponse;
type IBrokerBClient = BrokerApi.IBrokerBClient;
type IBrokerSettings = BrokerApi.IBrokerSettings;

enum QueryKeys {
    GetBroker = 'broker',
    GetBrokerBClients = 'brokerBClients',
    GetUserAgreements = 'userAgreements',
    GetBrokerSettings = 'brokerSettings',
}

export type BrokerErrorType = 'BROKER_UPDATE_FAILED' | 'BROKER_DUPLICATE';

export class BrokerMutationError extends Error {
    constructor(public readonly type: BrokerErrorType) {
        super(type);
        this.name = 'BrokerMutationError';
    }
}

const brokerApi = new BrokerApi.BrokerApi(applicationInsights);
const usersApi = new UsersApi.UsersApi(applicationInsights);
const fileApi = new FileApi.FileApi(applicationInsights);

export const useBrokerDetailsQuery = (brokerId: number | undefined, isAdmin: boolean) =>
    useQuery<IBrokerAdminResponse | IBrokerResponse | undefined>({
        queryKey: [QueryKeys.GetBroker, brokerId],
        enabled: !!brokerId,
        queryFn: async () => {
            if (!brokerId) return undefined;

            const response = isAdmin ? await brokerApi.getAdminSingleBroker(brokerId) : await brokerApi.getSingleBroker(brokerId);

            if (response.statusCode === 200) return response.result;

            throw new Error('Failed to retrieve broker.');
        },
    });

export const useBrokerBClientsQuery = (brokerId: number | undefined) =>
    useQuery<IBrokerBClient[]>({
        queryKey: [QueryKeys.GetBrokerBClients, brokerId],
        enabled: !!brokerId,
        queryFn: async () => {
            if (!brokerId) return [];

            const response = await brokerApi.getBrokerBClients(brokerId);

            if (response.statusCode === 200) return response.result;

            throw new Error('Failed to retrieve broker B-Clients.');
        },
    });

export const useSaveBrokerMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ data, isAdmin }: { data: BrokerApi.IUpdateBrokerPost; isAdmin: boolean }) => {
            if (data.id) {
                // Update existing broker
                const response = isAdmin ? await brokerApi.updateAdminBroker(data) : await brokerApi.updateBroker(data);
                if (response.statusCode !== 200) {
                    throw new BrokerMutationError('BROKER_UPDATE_FAILED');
                }
            } else {
                // Create new broker (admin only)
                if (!isAdmin) {
                    throw new Error('Brokers cannot create brokers.');
                }

                const validateResponse = await brokerApi.validateExistingBroker(data);
                if (validateResponse.statusCode !== 200) {
                    throw new Error('Validation failed.');
                }
                if (!validateResponse.result) {
                    throw new BrokerMutationError('BROKER_DUPLICATE');
                }

                const newBroker = await brokerApi.createBroker(data);
                if (newBroker.statusCode !== 200) {
                    throw new Error('Failed to create broker.');
                }
            }
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetBroker, variables.data.id] });
        },
    });
};

export const useActivateBrokerMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ brokerId, isReactivation }: { brokerId: number; isReactivation: boolean }) => {
            const response = isReactivation
                ? await brokerApi.reactivateBroker(brokerId)
                : await brokerApi.activateBroker(brokerId);

            if (response.statusCode !== 200) {
                throw new Error('Failed to activate Broker');
            }

            return response.result;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetBroker, variables.brokerId] });
        },
    });
};

export const useRejectBrokerMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ brokerId, comment }: { brokerId: number; comment: string }) => {
            const response = await brokerApi.rejectBroker(brokerId, comment);

            if (response.statusCode !== 200) {
                throw new Error('Reject call failed');
            }

            return response.result;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetBroker, variables.brokerId] });
        },
    });
};

export const useGetUserAgreementsQuery = (brokerId: number | undefined) =>
    useQuery<UsersApi.IUserAgreement[]>({
        queryKey: [QueryKeys.GetUserAgreements, brokerId],
        enabled: !!brokerId,
        queryFn: async () => {
            if (!brokerId) return [];

            const response = await usersApi.getUserAgreements(brokerId);

            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve user agreements.');
            }

            return response.result.results;
        },
    });

export const useCreateUserAgreementPdfMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (agreementId: number) => {
            const response = await usersApi.createUserAgreementPdf(agreementId);

            if (response?.statusCode !== 200) {
                throw new Error('Failed to create PDF.');
            }

            return response.result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetUserAgreements] });
        },
    });
};

export const downloadAgreementPdf = async (token: string) => {
    const response = await fileApi.downloadUrl({ token });
    if (response.statusCode !== 200) return;
    window.location.href = response.result.downloadUrl;
};

export const useBrokerSettingsQuery = (brokerId: number | undefined) =>
    useQuery<IBrokerSettings | undefined>({
        queryKey: [QueryKeys.GetBrokerSettings, brokerId],
        enabled: !!brokerId,
        queryFn: async () => {
            if (!brokerId) return undefined;

            const response = await brokerApi.getBrokerSettings(brokerId);

            if (response.statusCode === 200) return response.result;

            throw new Error('Failed to retrieve broker settings.');
        },
    });

export const useUploadCompanyLogoMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file }: { brokerId: number; file: File }) => {
            const response = await brokerApi.uploadCompanyLogo(file);

            if (response.statusCode !== 200) {
                throw new Error('Upload Company Logo call failed');
            }

            return response.result;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetBroker, variables.brokerId] });
        },
    });
};

export const useBrokerSettingsUpdateMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ data }: { brokerId: number; data: IBrokerSettings }) => {
            const response = await brokerApi.updateBrokerSettings(data);

            if (response.statusCode !== 200) {
                throw new Error('Update broker settings call failed');
            }

            return response.result;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.GetBroker, variables.brokerId] });
        },
    });
};
